import React, { useState, useCallback, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Terminal as TerminalIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import ConfirmDialog from './ConfirmDialog';
import SnackbarAlert from './SnackbarAlert';
import { useSnackbar } from '../hooks/useSnackbar';
import { useAwsAccountService } from '../../features/aws-account/services/awsAccountService';
import { extractErrorMessage } from '../utils/errorUtils';

export interface AWSCliCredentialsProps {
  customerId: string;
  accountId: string;
  allowWrite?: boolean;
  disabled?: boolean;
}

type CredFormat = 'macOS / Linux' | 'Windows' | 'PowerShell';

interface Credentials {
  access_key_id: string;
  secret_access_key: string;
  session_token: string;
  expiration: string;
  region: string;
}

// Keyed by the full fetch identity — mirrors getCliCredentials(customerId, accountId, roleType).
interface CachedCredentials {
  key: string;
  creds: Credentials;
}

function credsKey(customerId: string, accountId: string): string {
  return `${customerId}/${accountId}`;
}

function formatCredentials(creds: Credentials, format: CredFormat): string {
  switch (format) {
    case 'macOS / Linux':
      return [
        `export AWS_ACCESS_KEY_ID=${creds.access_key_id}`,
        `export AWS_SECRET_ACCESS_KEY=${creds.secret_access_key}`,
        `export AWS_SESSION_TOKEN=${creds.session_token}`,
        `export AWS_DEFAULT_REGION=${creds.region}`,
      ].join('\n');
    case 'Windows':
      return [
        `set AWS_ACCESS_KEY_ID=${creds.access_key_id}`,
        `set AWS_SECRET_ACCESS_KEY=${creds.secret_access_key}`,
        `set AWS_SESSION_TOKEN=${creds.session_token}`,
        `set AWS_DEFAULT_REGION=${creds.region}`,
      ].join('\n');
    case 'PowerShell':
      return [
        `$Env:AWS_ACCESS_KEY_ID="${creds.access_key_id}"`,
        `$Env:AWS_SECRET_ACCESS_KEY="${creds.secret_access_key}"`,
        `$Env:AWS_SESSION_TOKEN="${creds.session_token}"`,
        `$Env:AWS_DEFAULT_REGION="${creds.region}"`,
      ].join('\n');
  }
}

const FORMATS: CredFormat[] = ['macOS / Linux', 'Windows', 'PowerShell'];

const AWSCliCredentials: React.FC<AWSCliCredentialsProps> = ({
  customerId,
  accountId,
  allowWrite = false,
  disabled = false,
}) => {
  const awsAccountService = useAwsAccountService();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [copiedFormat, setCopiedFormat] = useState<CredFormat | null>(null);
  const [readonlyCreds, setReadonlyCreds] = useState<CachedCredentials | null>(null);
  const [writeCreds, setWriteCreds] = useState<CachedCredentials | null>(null);
  const [selectedRoleType, setSelectedRoleType] = useState<'readonly' | 'write'>('readonly');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmWriteDialog, setConfirmWriteDialog] = useState(false);

  // The parent rebinds this component to another customer/account without
  // remounting it, so the write confirmation must not carry over to the new
  // binding, and an in-flight fetch needs the ref to detect it no longer
  // matches. Layout effect: a passive effect would leave a window between the
  // rebind commit and the ref update in which a resolving fetch still sees the
  // old binding as current.
  const boundKeyRef = useRef(credsKey(customerId, accountId));
  useLayoutEffect(() => {
    boundKeyRef.current = credsKey(customerId, accountId);
    setSelectedRoleType('readonly');
    setCopiedFormat(null);
    setConfirmWriteDialog(false);
  }, [customerId, accountId]);

  const boundKey = credsKey(customerId, accountId);
  const activeEntry = selectedRoleType === 'write' ? writeCreds : readonlyCreds;
  const activeCreds = activeEntry?.key === boundKey ? activeEntry.creds : null;

  const fetchAndCopy = useCallback(async (format: CredFormat, roleType: 'readonly' | 'write') => {
    setLoading(true);
    setCopiedFormat(null);
    try {
      const key = credsKey(customerId, accountId);
      const cached = roleType === 'write' ? writeCreds : readonlyCreds;
      // The cache entry is tagged with the binding it was fetched for: a rebind
      // (or a fetch that resolves after one) must never serve the old creds.
      let credentials = cached?.key === key ? cached.creds : null;
      if (!credentials) {
        const result = await awsAccountService.getCliCredentials(customerId, accountId, roleType);
        // Rebound while the fetch was in flight: discard entirely — the stale
        // result must neither reach the clipboard nor clobber a cache entry
        // the new binding may have populated meanwhile.
        if (boundKeyRef.current !== key) {
          return;
        }
        credentials = result;
        const entry = { key, creds: result };
        if (roleType === 'write') {
          setWriteCreds(entry);
          setTimeout(() => setWriteCreds((prev) => (prev === entry ? null : prev)), 50 * 60 * 1000);
        } else {
          setReadonlyCreds(entry);
          setTimeout(() => setReadonlyCreds((prev) => (prev === entry ? null : prev)), 50 * 60 * 1000);
        }
      }
      const text = formatCredentials(credentials, format);
      await navigator.clipboard.writeText(text);
      setCopiedFormat(format);
      setTimeout(() => setCopiedFormat(null), 2000);
    } catch (error) {
      showSnackbar(extractErrorMessage(error, 'Failed to get CLI credentials'), 'error');
    } finally {
      setLoading(false);
    }
  }, [customerId, accountId, readonlyCreds, writeCreds, awsAccountService, showSnackbar]);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleSelectReadonly = useCallback(() => {
    setSelectedRoleType('readonly');
    handleMenuClose();
  }, [handleMenuClose]);

  const handleSelectWriteRequest = useCallback(() => {
    setConfirmWriteDialog(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleWriteAccessConfirm = useCallback(() => {
    setSelectedRoleType('write');
    setConfirmWriteDialog(false);
  }, []);

  const expiration = activeCreds?.expiration;

  if (!allowWrite) {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TerminalIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          {FORMATS.map((format) => (
            <Button
              key={format}
              size="small"
              variant="outlined"
              onClick={() => fetchAndCopy(format, 'readonly')}
              disabled={loading || disabled}
              startIcon={
                loading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : copiedFormat === format ? (
                  <CheckIcon sx={{ fontSize: 12 }} />
                ) : (
                  <CopyIcon sx={{ fontSize: 12 }} />
                )
              }
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.25,
                fontSize: '0.7rem',
                textTransform: 'none',
                borderColor: copiedFormat === format ? 'success.main' : 'divider',
                color: copiedFormat === format ? 'success.main' : 'text.secondary',
              }}
            >
              {format}
            </Button>
          ))}
          {readonlyCreds?.key === boundKey && (
            <Chip
              label={`expires ${new Date(readonlyCreds.creds.expiration).toLocaleTimeString()}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
          )}
        </Box>
        <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} autoHideDuration={6000} />
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '4px',
            overflow: 'hidden',
          }}
        >
          <TerminalIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5 }} />
          <Button
            size="small"
            variant="text"
            onClick={handleMenuOpen}
            disabled={loading}
            sx={{
              minWidth: 'auto',
              px: 0.5,
              py: 0.25,
              fontSize: '0.7rem',
              color: selectedRoleType === 'write' ? 'warning.main' : 'text.secondary',
              '&:hover': { color: selectedRoleType === 'write' ? 'warning.dark' : 'primary.main', bgcolor: 'action.hover' },
            }}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          >
            {selectedRoleType === 'write' ? 'Write' : 'Readonly'}
          </Button>
        </Box>

        {FORMATS.map((format) => (
          <Button
            key={format}
            size="small"
            variant="outlined"
            onClick={() => fetchAndCopy(format, selectedRoleType)}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={12} color="inherit" />
              ) : copiedFormat === format ? (
                <CheckIcon sx={{ fontSize: 12 }} />
              ) : (
                <CopyIcon sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              fontSize: '0.7rem',
              textTransform: 'none',
              borderColor: copiedFormat === format ? 'success.main' : selectedRoleType === 'write' ? 'warning.main' : 'divider',
              color: copiedFormat === format ? 'success.main' : selectedRoleType === 'write' ? 'warning.main' : 'text.secondary',
            }}
          >
            {format}
          </Button>
        ))}

        {expiration && (
          <Chip
            label={`expires ${new Date(expiration).toLocaleTimeString()}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleSelectReadonly}>
          <Typography variant="body2">Readonly Access</Typography>
        </MenuItem>
        <MenuItem onClick={handleSelectWriteRequest}>
          <Typography variant="body2" color="warning.main">
            Write Access
          </Typography>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={confirmWriteDialog}
        title="Write Access Warning"
        onCancel={() => setConfirmWriteDialog(false)}
        onConfirm={handleWriteAccessConfirm}
        confirmText="OK - Use Write Credentials"
        confirmColor="warning"
      >
        You are about to copy CLI credentials with <strong>write permissions</strong>.
        <br />
        <br />
        This allows you to make changes to AWS resources. Are you sure you want to proceed?
      </ConfirmDialog>

      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} autoHideDuration={6000} />
    </>
  );
};

export default AWSCliCredentials;
